import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import AnalyticsCard from './AnalyticsCard';
import { 
  Eye, Users, Phone, TrendingUp, Clock, Smartphone, 
  Monitor, Tablet, BarChart3, Calendar, RefreshCw, ChevronDown
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsDashboard = ({ token }) => {
  const [analytics, setAnalytics] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('today');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);

  // Date period options
  const periodOptions = [
    { value: 'today', label: 'Today', days: 1 },
    { value: '3days', label: '3 Days', days: 3 },
    { value: '7days', label: '7 Days', days: 7 },
    { value: '30days', label: '30 Days', days: 30 },
    { value: 'custom', label: 'Custom Range', days: null }
  ];

  const fetchAnalytics = async () => {
    try {
      setIsLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      
      let days = 7; // default
      let startDate = null;
      let endDate = null;
      
      // Calculate date range based on selected period
      if (selectedPeriod === 'custom' && customDateRange.startDate && customDateRange.endDate) {
        startDate = customDateRange.startDate;
        endDate = customDateRange.endDate;
        const start = new Date(startDate);
        const end = new Date(endDate);
        days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      } else {
        const selectedOption = periodOptions.find(opt => opt.value === selectedPeriod);
        days = selectedOption ? selectedOption.days : 7;
      }
      
      // Build API URL with parameters
      let apiUrl = `${API}/admin/analytics?days=${days}`;
      if (startDate && endDate) {
        apiUrl += `&start_date=${startDate}&end_date=${endDate}`;
      }
      
      // Fetch summary analytics
      const summaryResponse = await axios.get(apiUrl, { headers });
      setAnalytics(summaryResponse.data);
      
      // Fetch daily analytics
      let dailyApiUrl = `${API}/admin/analytics/daily?days=${days}`;
      if (startDate && endDate) {
        dailyApiUrl += `&start_date=${startDate}&end_date=${endDate}`;
      }
      
      const dailyResponse = await axios.get(dailyApiUrl, { headers });
      setDailyData(dailyResponse.data);
      
      // Fetch comparison data (previous period)
      if (selectedPeriod !== 'today') {
        try {
          const comparisonApiUrl = `${API}/admin/analytics?days=${days}&offset=${days}`;
          const comparisonResponse = await axios.get(comparisonApiUrl, { headers });
          setComparisonData(comparisonResponse.data);
        } catch (error) {
          console.log('Comparison data not available');
        }
      }
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Gagal load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchAnalytics();
    }
  }, [token, selectedPeriod, customDateRange.startDate, customDateRange.endDate]);

  // Calculate percentage change
  const calculateChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  // Handle period change
  const handlePeriodChange = (newPeriod) => {
    setSelectedPeriod(newPeriod);
    if (newPeriod !== 'custom') {
      setShowCustomDate(false);
      setCustomDateRange({ startDate: '', endDate: '' });
    } else {
      setShowCustomDate(true);
    }
  };

  // Handle custom date change
  const handleCustomDateChange = (field, value) => {
    setCustomDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Get period label for display
  const getPeriodLabel = () => {
    if (selectedPeriod === 'custom' && customDateRange.startDate && customDateRange.endDate) {
      return `${customDateRange.startDate} to ${customDateRange.endDate}`;
    }
    const selectedOption = periodOptions.find(opt => opt.value === selectedPeriod);
    return selectedOption ? selectedOption.label : '7 Days';
  };

  const formatHour = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    if (hour === 12) return '12 PM';
    return `${hour - 12} PM`;
  };

  const getDeviceIcon = (device) => {
    switch (device) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-20">
        <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-400" />
        <p className="text-slate-600">No analytics data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-3xl font-bold text-slate-900">Website Analytics</h2>
            <p className="text-slate-600 mt-1">Monitor traffic, engagement, dan conversions</p>
          </div>
          <Button onClick={fetchAnalytics} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Date Filter Buttons */}
        <Card className="p-4">
          <div className="flex flex-col space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-slate-600" />
                <span className="font-semibold text-slate-900">Date Range:</span>
                <Badge variant="secondary" className="text-sm">
                  {getPeriodLabel()}
                </Badge>
              </div>
            </div>
            
            {/* Quick Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              {periodOptions.map((option) => (
                <Button
                  key={option.value}
                  onClick={() => handlePeriodChange(option.value)}
                  variant={selectedPeriod === option.value ? "default" : "outline"}
                  size="sm"
                  className={selectedPeriod === option.value ? "bg-blue-600" : ""}
                >
                  {option.label}
                </Button>
              ))}
            </div>

            {/* Custom Date Range Inputs */}
            {showCustomDate && (
              <div className="flex flex-col sm:flex-row gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customDateRange.startDate}
                    onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customDateRange.endDate}
                    onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <AnalyticsCard
          title="Today's Views"
          value={analytics.today_views?.toLocaleString() || '0'}
          subtitle="Page views hari ini"
          icon={Eye}
          color="blue"
        />
        <AnalyticsCard
          title="Today's Visitors"
          value={analytics.today_visitors?.toLocaleString() || '0'}
          subtitle="Unique visitors hari ini"
          icon={Users}
          color="green"
        />
        <AnalyticsCard
          title="WhatsApp Clicks"
          value={analytics.today_whatsapp_clicks?.toLocaleString() || '0'}
          subtitle="Hari ini"
          icon={Phone}
          color="purple"
        />
        <AnalyticsCard
          title="Active Now"
          value={analytics.active_sessions?.toLocaleString() || '0'}
          subtitle="Live visitors"
          icon={Clock}
          color="orange"
        />
      </div>

      {/* Conversion Rate */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span>Conversion Rate</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <div className="text-4xl font-bold text-green-600 mb-2">
              {analytics.conversion_rate}%
            </div>
            <p className="text-slate-600">Model views → WhatsApp clicks</p>
            <div className="mt-4 text-sm text-slate-500">
              Higher is better. Industry average: 2-5%
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Popular Models */}
        <Card>
          <CardHeader>
            <CardTitle>Popular Car Models</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {analytics.popular_models?.map((model, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center text-xs font-medium">
                      {index + 1}
                    </div>
                    <span className="font-medium capitalize">{model.model}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{model.views}</div>
                    <div className="text-xs text-slate-500">views</div>
                  </div>
                </div>
              )) || <p className="text-slate-500 text-center py-4">No data available</p>}
            </div>
          </CardContent>
        </Card>

        {/* Device Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Device Types</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(analytics.device_stats || {}).map(([device, count]) => (
                <div key={device} className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {getDeviceIcon(device)}
                    <span className="font-medium capitalize">{device}</span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold">{count}</div>
                    <div className="text-xs text-slate-500">visitors</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Hourly Traffic */}
      <Card>
        <CardHeader>
          <CardTitle>Hourly Traffic (Last 24 Hours)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-12 gap-2 text-center">
            {Array.from({ length: 24 }, (_, hour) => {
              const hourData = analytics.hourly_traffic?.find(h => h.hour === hour);
              const views = hourData?.views || 0;
              const maxViews = Math.max(...(analytics.hourly_traffic?.map(h => h.views) || [1]));
              const height = maxViews > 0 ? (views / maxViews) * 100 : 0;
              
              return (
                <div key={hour} className="flex flex-col items-center">
                  <div className="text-xs text-slate-500 mb-1">
                    {formatHour(hour)}
                  </div>
                  <div className="w-full bg-slate-100 rounded-t" style={{ height: '60px' }}>
                    <div 
                      className="w-full bg-blue-500 rounded-t transition-all duration-300"
                      style={{ height: `${height}%`, marginTop: `${100 - height}%` }}
                      title={`${formatHour(hour)}: ${views} views`}
                    />
                  </div>
                  <div className="text-xs font-medium mt-1">
                    {views}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Daily Stats Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="w-5 h-5" />
            <span>Daily Breakdown</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Date</th>
                  <th className="text-left p-2">Page Views</th>
                  <th className="text-left p-2">Visitors</th>
                  <th className="text-left p-2">WhatsApp Clicks</th>
                  <th className="text-left p-2">Popular Model</th>
                  <th className="text-left p-2">Peak Hour</th>
                </tr>
              </thead>
              <tbody>
                {dailyData?.map((day) => (
                  <tr key={day.date} className="border-b hover:bg-slate-50">
                    <td className="p-2 font-medium">
                      {new Date(day.date).toLocaleDateString('en-GB')}
                    </td>
                    <td className="p-2">{day.page_views}</td>
                    <td className="p-2">{day.unique_visitors}</td>
                    <td className="p-2">{day.whatsapp_clicks}</td>
                    <td className="p-2">
                      {day.popular_models?.[0] ? (
                        <Badge variant="secondary" className="text-xs">
                          {day.popular_models[0].model} ({day.popular_models[0].views})
                        </Badge>
                      ) : '-'}
                    </td>
                    <td className="p-2">
                      {day.peak_hour !== null ? formatHour(day.peak_hour) : '-'}
                    </td>
                  </tr>
                )) || (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-slate-500">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;